import type { EntityLevel } from '@sl/core';
import type { IconName } from '../ui';

// EVERY WORD GLOBAL SEARCH WRITES, in one file (T-044) — the same rule the list's copy keeps (T-230): a
// sentence with a key in the message catalogue is never retyped here, and what is left is the palette's own
// chrome, written to the voice rules (DESIGN_SYSTEM §2) and scanned by the SC-5 glossary test.
//
// Nothing here is a Meta term: the levels are "Campaigns", "Ad sets" and "Ads" (REQ-043 AC2), and the
// keyboard hints name keys, not jargon.

/** The group heading and the level pill, per level (`@sl/core` spells the levels `campaign | adset | ad`). */
export interface LevelWords {
  /** The group heading: "Ad sets". */
  plural: string;
  /** The row's level pill: "Ad set". */
  singular: string;
  /** DESIGN_SYSTEM §12 row 31 draws an entity icon on every row. */
  icon: IconName;
}

export const SEARCH_LEVEL_WORDS: Readonly<Record<EntityLevel, LevelWords>> = {
  campaign: { plural: 'Campaigns', singular: 'Campaign', icon: 'megaphone' },
  adset: { plural: 'Ad sets', singular: 'Ad set', icon: 'users' },
  ad: { plural: 'Ads', singular: 'Ad', icon: 'images' },
};

export const SEARCH_COPY = {
  /** The top bar's button (DESIGN_SYSTEM §10: "search / command palette field with the Ctrl/⌘ K hint"). */
  trigger: 'Search',
  /** Its accessible name — the words alone do not say what is searched, and below 980 px there are no words. */
  triggerLabel: 'Search campaigns, ad sets and ads',
  /** Its tooltip: the two ways in. `⌘` is substituted for `Ctrl` on a Mac at render time. */
  triggerHint: 'Search campaigns, ad sets and ads. Press / or Ctrl K.',
  triggerHintMac: 'Search campaigns, ad sets and ads. Press / or ⌘ K.',
  /** The dialog's accessible name. The panel draws no heading — the input is the heading (row 31). */
  title: 'Search',
  inputLabel: 'Search campaigns, ad sets and ads',
  inputPlaceholder: 'Search by name…',
  close: 'Close search',
  /** The listbox's accessible name; it is not the input's label, and a listbox with neither is anonymous. */
  resultsLabel: 'Search results',
  /** Above the last five things opened, when nothing is typed. */
  recent: 'Recent',
  /** Nothing typed and nothing opened before: say what to do rather than showing an empty box. */
  prompt: 'Type a name to find a campaign, ad set or ad.',
  /** The empty state (row 31's "no results"). `{query}` is what was typed, in quotes. */
  emptyTitle: (query: string) => `Nothing matches “${query}”`,
  emptyBody: 'Try part of a campaign, ad set or ad name.',
  /** The hint row along the foot of the palette. */
  hintMove: 'to move',
  hintOpen: 'to open',
  hintClose: 'to close',
  /** What the parent line says on an ad set or an ad — "in Fall Collection – Prospecting". */
  parent: (name: string) => `in ${name}`,
  /** Still loading the index: the palette opens at once and says so rather than looking empty. */
  loading: 'Loading your campaigns…',
  /** The index could not be read. The palette is the only thing affected, so it says only that. */
  error: 'We couldn’t load your campaigns, so search has nothing to look through.',
  retry: 'Try again',
} as const;

/** "n more ad sets" — the tail of a group that is holding results back (REQ-042 AC1's grouping). */
export function moreInGroup(more: number, level: EntityLevel): string {
  const words = SEARCH_LEVEL_WORDS[level];
  return `${more} more ${more === 1 ? words.singular.toLowerCase() : words.plural.toLowerCase()}`;
}

/** What the group heading counts: "Ad sets" + "2". The count is its own element so it can be muted. */
export function groupHeading(level: EntityLevel): string {
  return SEARCH_LEVEL_WORDS[level].plural;
}

/** REQ-281: the number of results reaches a screen reader through the app's ONE live region. */
export function resultsAnnouncement(total: number, query: string): string {
  if (total === 0) return SEARCH_COPY.emptyTitle(query);
  return total === 1 ? '1 result' : `${total} results`;
}

/** A result's accessible name: the name, what it is, where it lives, and whether it is on. */
export function optionLabel(
  name: string,
  level: EntityLevel,
  parentName: string | null,
  statusLabel: string,
): string {
  const words = SEARCH_LEVEL_WORDS[level];
  const where = parentName === null ? '' : `, ${SEARCH_COPY.parent(parentName)}`;
  return `${name} — ${words.singular}${where}, ${statusLabel}`;
}
